import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  hasSelectedChannel,
  createChannelSelection,
  setChannelSelection,
  type ChannelSelection,
} from "./consent-preferences";
import {
  type PurposeDefinition,
  type ChannelDefinition,
} from "./use-consent-content";

type Props = {
  purpose: PurposeDefinition;
  channels: ChannelDefinition[];
  selection: ChannelSelection;
  onChange: (selection: ChannelSelection) => void;
};

export function ConsentPurposeCard({
  purpose,
  channels,
  selection,
  onChange,
}: Props): ReactElement {
  const { t } = useTranslation();
  const isActive = hasSelectedChannel(selection);
  return (
    <section className="consent-platform-purpose" data-active={isActive}>
      <div className="consent-platform-purpose-heading">
        <div className="consent-platform-purpose-details">
          <span className="consent-platform-purpose-icon">{purpose.icon}</span>
          <span>
            <strong>{purpose.title}</strong>
            <span className="consent-platform-purpose-description">
              {purpose.description}
            </span>
          </span>
        </div>
        <input
          className="consent-platform-switch"
          type="checkbox"
          role="switch"
          aria-checked={isActive}
          aria-controls={`consent-channels-${purpose.id}`}
          aria-label={purpose.title}
          checked={isActive}
          onChange={(event) => {
            const isAccepted = event.target.checked;
            onChange(createChannelSelection({ isAccepted }));
          }}
        />
      </div>
      <div id={`consent-channels-${purpose.id}`} hidden={!isActive}>
        <fieldset className="consent-platform-channels" disabled={!isActive}>
          <legend>{t("Canaux de réception")}</legend>
          {channels.map((channel) => (
            <label key={channel.id} className="consent-platform-channel">
              <input
                type="checkbox"
                checked={selection[channel.id]}
                onChange={(event) =>
                  onChange(
                    setChannelSelection(selection, {
                      channel: channel.id,
                      isAccepted: event.target.checked,
                    }),
                  )
                }
              />
              {channel.icon}
              <span>
                {channel.label}
                {channel.id === "email" && (
                  <small>{t("(avec pixels de suivi)")}</small>
                )}
              </span>
            </label>
          ))}
        </fieldset>
      </div>
    </section>
  );
}
